var _dec, _dec2, _dec3, _dec4, _dec5, _class, _class2;
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
import { tagName, layout as templateLayout } from '@ember-decorators/component';
import { action, computed } from '@ember/object';
import Component from '@ember/component';
import { isNone } from '@ember/utils';
import { cancel, later } from '@ember/runloop';
import layout from '../templates/components/fly-dropdown';
import { DOWN, UP } from '@customerio/fly-components/utils/keyboard-support';
import itemProperty from '@customerio/fly-components/utils/item-property';
import { A } from '@ember/array';
import FlyMenuItem from '@customerio/fly-components/components/fly-menu-item';

/**
  The fly-dropdown component is a more flexible select widget. While we prefer
  native select where possible, there are some cases where a dropdown is better.

  The dropdown attempts to mirror the same keyboard navigation you're familiar
  with from using native select elements. When the toggle button becomes
  focused, up, down, enter, and space will open the dropdown. Once the dropdown
  is expanded, the up and down arrows navigate focus within the list. An item
  can be selected by pressing enter or space.

  ```hbs
  <FlyDropdown
    /@items={{items}}
    /@selectedValue={{selectedValue}} />
  ```

  @class FlyDropdown
  @public
 */
let FlyDropdown = (_dec = tagName(''), _dec2 = templateLayout(layout), _dec3 = computed('selectedValue', 'items.[]'), _dec4 = computed('items.[]'), _dec5 = computed('prompt', '_selectedItem'), _dec(_class = _dec2(_class = (_class2 = class FlyDropdown extends Component.extend({
  // Public interface
  /**
    The list of items to display in the dropdown menu. It should be an array of
    object with a `value` and `label` key.
     ```json
    const options = [
      { value: 'dmr', label: 'Dennis Ritchie' },
      { value: 'gbmh', label: 'Grace Hopper' },
      { value: 'mhh', label: 'Margaret Hamilton' },
      { value: 'rob', label: 'Rob Pike' }
    ];
    ```
     @argument items
    @type array
   */
  items: null // left in Ember Object class .extend() call: can be both computed property (get items() ...) or class instance property (items = ...) on any child class, which would break with native class syntax
}) {
  constructor(...args) {
    super(...args);
    // Public interface
    /**
      The selected value for the `select` element. It corresponds to a `value` key
      in the `items` array.
       @argument selectedValue
      @type string
     */
    _defineProperty(this, "selectedValue", null);
    /**
      The string of text to display when a value hasn’t been set yet. It should be
      brief and explain what they will be selecting.
        @argument prompt
      @type string
     */
    _defineProperty(this, "prompt", null);
    /**
      The component to render for each item rather than a plain `li`.
      With this you can add more than one piece of imformation per line item,
      color them differently, etc.
       ```hbs
      <FlyDropdown /@itemComponent={{component "components/menu-item"}} />
      ```
       @argument itemComponent
      @type Component
     */
    _defineProperty(this, "itemComponent", FlyMenuItem);
    /**
      Display a message when no items have been passed in to the component. This
      is especially useful when the items in a dropdown will be dynamic, and
      could be empty.
       @argument emptyMessage
      @type string
     */
    _defineProperty(this, "emptyMessage", "Oops! There's nothing to choose from...");
    /**
      Whether the select element is currently disabled or not.
       @argument isDisabled
      @type boolean
     */
    _defineProperty(this, "isDisabled", false);
    /**
      The custom property to use as the value key when rendering the list of
      `items`.. By default, `value` is used. This can be useful for a list of
      records from a database that have unique `ID`s.
       @argument valueProperty
      @type string
     */
    _defineProperty(this, "valueProperty", 'value');
    /**
      The custom property to use as the label key when rendering the list of
      `items`.. By default, `label` is used.
       @argument labelProperty
      @type string
     */
    _defineProperty(this, "labelProperty", 'label');
    /**
      Whether the field has an error associated to it. This is usually from
      validation, but could be from anything.
       @argument hasError
      @type boolean
     */
    _defineProperty(this, "hasError", false);
    /**
      The error message to display when an error has occured. The prescence of
      this attribute does not signal that an error has occured. For the error
      message to be visible, you must also set `hasError`.
       @argument errorMessage
      @type string
     */
    _defineProperty(this, "errorMessage", null);
    /**
      TODO: Document this prop
       @argument renderInPlace
      @type boolean
     */
    _defineProperty(this, "renderInPlace", true);
    // Private state and methods
    _defineProperty(this, "isOpen", false);
    _defineProperty(this, "focusedItem", null);
    _defineProperty(this, "rootElement", null);
  }
  // "_selectedItem" is used internally, but it's synced with external "selectedItem" binding
  get _selectedItem() {
    let {
      selectedValue,
      items,
      valueProperty
    } = this.getProperties('selectedValue', 'items', 'valueProperty');
    if (isNone(items)) {
      return null;
    }
    return items.find(item => {
      let value = itemProperty(item, valueProperty);
      return value === selectedValue;
    });
  }
  set _selectedItem(item) {
    let {
      valueProperty
    } = this;
    this.set('selectedValue', itemProperty(item, valueProperty));

    // update external "selectedItem" binding if necessary:
    if (item !== this.selectedItem) {
      this.set('selectedItem', item);
    }
    return item;
  }
  /*
    The list of items to display in the dropdown. Override in a subclass if
    you want to show a subset of the full list, e.g. filtered by a search
    query.
  */
  get visibleItems() {
    return A(this.items);
  }

  /*
    Display the selected item or the prompt if none selected.
   */
  get computedPrompt() {
    let selectedItem = this._selectedItem;
    let {
      prompt
    } = this;
    let {
      labelProperty
    } = this;
    return selectedItem ? itemProperty(selectedItem, labelProperty) : prompt;
  }

  /*
    Open the dropdown menu and focus the currently selected item.
   */
  expand() {
    let selectedItem = this._selectedItem;
    this.setProperties({
      isOpen: true,
      focusedItem: selectedItem
    });
  }

  /*
    Try to focus and scroll to the given item, if the item exists. Ignores
    `null` or `undefined` items, so that callers can ignore array indices when
    focusing the next or matching item.
  */
  _navigateToItem(item) {
    if (!isNone(item)) {
      this.set('focusedItem', item);
    }
  }

  /*
    Process a key event to continue a type-ahead find of items, based on
    the search string built up from previous key events. If keypresses arrive
    within 200ms, they are combined into a search string. After 200ms, the
    string is cleared so that type-ahead can start again.
  */
  _typeToFocus(which) {
    cancel(this._clearTypeToFocusQueryFunc);
    let c = String.fromCharCode(which).toLowerCase();
    let currentTypeToFocusQuery = (this._currentTypeToFocusQuery || '') + c;
    let {
      labelProperty
    } = this;
    let matchingItems = this.visibleItems.filter(item => {
      let lowerCaseLabel = itemProperty(item, labelProperty).toLowerCase();
      return lowerCaseLabel.indexOf(currentTypeToFocusQuery) === 0;
    });
    this._navigateToItem(matchingItems[0]);
    this.setProperties({
      _currentTypeToFocusQuery: currentTypeToFocusQuery,
      _clearTypeToFocusQueryFunc: later(this, function () {
        // It is possible this timer could trigger after the component
        // has been destroyed so we must check for that.
        if (!this.isDestroyed) {
          this.set('_currentTypeToFocusQuery', '');
        }
      }, 200)
    });
  }
  didInsert(element) {
    this.rootElement = element;
  }
  didReceiveAttrs() {
    super.didReceiveAttrs();
    if (this.selectedItem !== this._selectedItem && !this._selectedItem) {
      this.set('_selectedItem', this.selectedItem);
    }
  }
  focusItem(item) {
    this.set('focusedItem', item);
  }
  navigateFocus(evt) {
    let direction = evt.which;
    if (direction === DOWN || direction === UP) {
      if (!this.isOpen) {
        this.expand();
        return;
      }
      let currentFocus = this.focusedItem;
      if (!currentFocus) {
        this.set('focusedItem', this.visibleItems?.[0]);
        return;
      }
      let items = this.visibleItems;
      let currentFocusIndex = items.indexOf(currentFocus);
      let delta = direction === DOWN ? +1 : -1;
      this._navigateToItem(items[currentFocusIndex + delta]);
    } else {
      this._typeToFocus(evt.which);
    }
  }
  collapse() {
    this.set('isOpen', false);
  }
  onEnterPressed() {
    if (this.isOpen) {
      let {
        focusedItem
      } = this;
      if (!isNone(focusedItem)) {
        this.send('toggleSelection', focusedItem);
      }
    } else {
      this.expand();
    }
  }
  onFocusOut() {
    if (this.attrs['focus-out']) {
      this.attrs['focus-out']();
    }
  }
  toggle() {
    return this.isOpen ? this.collapse() : this.expand();
  }
  toggleSelection(item) {
    if (!this.onSelect || this.onSelect(item) !== false) {
      this.set('_selectedItem', item);
    }
    this.collapse();
  }
  findDropdown() {
    return this.rootElement;
  }
}, (_applyDecoratedDescriptor(_class2.prototype, "_selectedItem", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "_selectedItem"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "visibleItems", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "visibleItems"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "computedPrompt", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "computedPrompt"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "didInsert", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "didInsert"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "focusItem", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "focusItem"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "navigateFocus", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "navigateFocus"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "collapse", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "collapse"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "onEnterPressed", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "onEnterPressed"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "onFocusOut", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "onFocusOut"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggle", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "toggle"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleSelection", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleSelection"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "findDropdown", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "findDropdown"), _class2.prototype)), _class2)) || _class) || _class);
export { FlyDropdown as default };